import { Renderer, type RenderOptions } from './Renderer';
import { RenderLoop } from './RenderLoop';
import type { InputEvent } from '../input/InputProvider';

interface FrameBufferSource {
  frameBuffer: Uint8Array;
  statusIcons: Record<string, boolean>;
}

interface ButtonDefEntry {
  keyCode: string;
  label: string;
}

export class MinimalUI {
  readonly container: HTMLDivElement;
  readonly canvas: HTMLCanvasElement;
  readonly renderer: Renderer;
  readonly renderLoop: RenderLoop;
  onInput: ((event: InputEvent) => void) | null = null;

  private screenConfig: { cols: number; rows: number } = { cols: 256, rows: 63 };
  private frameBufferSource: (() => FrameBufferSource | null) | null = null;
  private pressedButtons: Set<string> = new Set();
  private scale = 2;
  private resizeObserver: ResizeObserver | null = null;
  private buttonGrid: HTMLDivElement;
  private buttonElements: Map<string, HTMLButtonElement> = new Map();
  private visible = false;
  private pointerHandlers: Map<string, {
    onDown: (e: PointerEvent) => void;
    onUp: (e: PointerEvent) => void;
    onLeave: (e: PointerEvent) => void;
  }> = new Map();

  constructor(container: HTMLElement) {
    this.container = document.createElement('div');
    this.container.className = 'minimal-ui-container ui-mode-fade';
    container.appendChild(this.container);

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'minimal-ui-canvas';
    this.canvas.style.display = 'block';
    this.canvas.style.imageRendering = 'pixelated';
    this.canvas.style.maxWidth = '100%';
    this.container.appendChild(this.canvas);

    this.buttonGrid = document.createElement('div');
    this.buttonGrid.className = 'minimal-ui-button-grid';
    this.container.appendChild(this.buttonGrid);

    this.renderer = new Renderer(this.canvas);
    this.renderLoop = new RenderLoop(this.renderer);

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(container);
    this.handleResize();
  }

  show(): void {
    this.container.classList.remove('hidden');
    this.visible = true;
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.visible = false;
  }

  isVisible(): boolean {
    return this.visible;
  }

  bindInputManager(inputManager: { attachAll: (el: HTMLElement) => void }): void {
    inputManager.attachAll(this.canvas);
  }

  setScreenConfig(config: { cols: number; rows: number }): void {
    this.screenConfig = config;
  }

  setFrameBufferSource(source: () => FrameBufferSource | null): void {
    this.frameBufferSource = source;
  }

  setButtons(buttons: ButtonDefEntry[]): void {
    this.clearButtonGrid();

    for (const btn of buttons) {
      const el = document.createElement('button');
      el.className = 'minimal-ui-button';
      el.textContent = btn.label;
      el.setAttribute('data-keycode', btn.keyCode);

      const onDown = (e: PointerEvent) => {
        e.preventDefault();
        this.setButtonPressed(btn.keyCode, true);
      };
      const onUp = (e: PointerEvent) => {
        e.preventDefault();
        this.setButtonPressed(btn.keyCode, false);
      };
      const onLeave = (_e: PointerEvent) => {
        if (this.pressedButtons.has(btn.keyCode)) {
          this.setButtonPressed(btn.keyCode, false);
        }
      };

      el.addEventListener('pointerdown', onDown);
      el.addEventListener('pointerup', onUp);
      el.addEventListener('pointerleave', onLeave);
      el.addEventListener('pointercancel', onLeave);

      this.pointerHandlers.set(btn.keyCode, { onDown, onUp, onLeave });
      this.buttonElements.set(btn.keyCode, el);
      this.buttonGrid.appendChild(el);
    }
  }

  setInterfaceImage(_image: ImageBitmap): void {
  }

  setPressedButtons(pressed: Set<string>): void {
    const prev = this.pressedButtons;
    this.pressedButtons = pressed;
    for (const key of prev) {
      if (!pressed.has(key)) {
        const el = this.buttonElements.get(key);
        if (el) el.classList.remove('pressed');
      }
    }
    for (const key of pressed) {
      if (!prev.has(key)) {
        const el = this.buttonElements.get(key);
        if (el) el.classList.add('pressed');
      }
    }
  }

  setScale(scale: number): void {
    this.scale = scale;
    this.handleResize();
  }

  start(): void {
    this.show();
    this.renderLoop.setFrameCallback(() => {
      const source = this.frameBufferSource?.() ?? null;
      if (!source) return null;

      const buttonsForRenderer = Array.from(this.buttonElements.entries()).map(([keyCode, el]) => ({
        id: keyCode,
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        label: el.textContent || keyCode,
      }));

      const options: RenderOptions = {
        frameBuffer: source.frameBuffer,
        screenConfig: this.screenConfig,
        statusIcons: source.statusIcons,
        buttons: buttonsForRenderer,
        pressedButtons: this.pressedButtons,
        mode: 'minimal',
        scale: this.scale,
      };

      return options;
    });

    this.renderLoop.start();
  }

  stop(): void {
    this.renderLoop.stop();
  }

  destroy(): void {
    this.stop();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.clearButtonGrid();

    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }

  private setButtonPressed(keyCode: string, pressed: boolean): void {
    const el = this.buttonElements.get(keyCode);
    if (!el) return;

    if (pressed) {
      this.pressedButtons = new Set([...this.pressedButtons, keyCode]);
      el.classList.add('pressed');
    } else {
      const next = new Set(this.pressedButtons);
      next.delete(keyCode);
      this.pressedButtons = next;
      el.classList.remove('pressed');
    }

    if (this.onInput) {
      this.onInput({
        keyCode,
        scanCode: 0,
        pressed,
        timestamp: Date.now(),
      });
    }
  }

  private clearButtonGrid(): void {
    for (const [keyCode, handlers] of this.pointerHandlers) {
      const el = this.buttonElements.get(keyCode);
      if (el) {
        el.removeEventListener('pointerdown', handlers.onDown);
        el.removeEventListener('pointerup', handlers.onUp);
        el.removeEventListener('pointerleave', handlers.onLeave);
        el.removeEventListener('pointercancel', handlers.onLeave);
      }
    }
    this.pointerHandlers.clear();
    this.buttonElements.clear();
    this.buttonGrid.innerHTML = '';
  }

  private handleResize(): void {
    const width = this.screenConfig.cols * 8 * this.scale;
    const height = this.screenConfig.rows * this.scale;
    this.renderer.setSize(width, height);
  }
}
